<template>
  <div>
    <!-- PIPELINE -->
    <div class="section mb-4">
      <div class="section-header mb-3">
        <span class="section-label">PIPELINE DE ENVÍOS — ESTADO + ANTIGÜEDAD</span>
        <span class="days-back-label">Todas las activas · últimos {{ days_back }} días</span>
      </div>

      <div class="container-skeleton-card" v-if="loading_shipment_status">
        <div v-for="n in 7" :key="n">
          <v-skeleton-loader type="card"></v-skeleton-loader>
        </div>
      </div>
      <div class="status-cards pipeline-cards" v-else>
        <div
          v-for="state in pipelineStates"
          :key="state.id_state"
          class="status-card"
          :class="'status-envio-' + state.id_state"
        >
          <div class="status-name">{{ pipelineCardName(state) }}</div>
          <div class="status-count">{{ state.amount }}</div>
        </div>
      </div>
    </div>

    <!-- TABLA DE ÓRDENES ACTIVAS -->
    <div class="section mb-4">
      <div class="section-header mb-2">
        <h6 class="mb-0">Órdenes activas</h6>
      </div>

      <!-- Filtros rápidos -->
      <div class="quick-filters mb-2">
        <button
          class="period-btn"
          :class="{ active: activeStateFilter === null && !filterStuck }"
          @click="activeStateFilter = null; filterStuck = false"
        >Todos ({{ activeOrders.length }})</button>
        <button
          v-for="state in pipelineStates"
          :key="state.id_state"
          class="period-btn"
          :class="{ active: activeStateFilter === state.id_state && !filterStuck }"
          :style="getFilterBtnStyle(state.id_state, activeStateFilter === state.id_state && !filterStuck)"
          @click="activeStateFilter = state.id_state; filterStuck = false"
        >{{ pipelineCardName(state) }} ({{ ordersCountByState(state.id_state) }})</button>
        <button
          v-if="stuckOrders.length > 0"
          class="period-btn period-btn--stuck"
          :class="{ active: filterStuck }"
          @click="filterStuck = !filterStuck; activeStateFilter = null"
        >Sin movimiento ({{ stuckOrders.length }})</button>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredActiveOrders"
        :loading="loading_shipment_today"
        loading-text="Cargando..."
        no-data-text="Sin órdenes activas"
        density="compact"
      >
        <template v-slot:item="{ item }">
          <tr :class="['status-envio-' + item.status?.id, { 'row-stuck': isStuck(item) }]">
            <td>{{ item.code }}</td>
            <td>{{ item.client?.user?.username }}</td>
            <td>
              {{ item.document_type?.name ?? "-" }}<br v-if="item.external_reference" />
              <span v-if="item.external_reference" class="table-text-secondary">Ref: {{ item.external_reference }}</span>
            </td>
            <td>{{ item.canal_venta?.name ?? "-" }}</td>
            <td>
              <span class="status-badge" :class="'status-envio-' + item.status?.id">
                {{ shipmentOrderService.getStatusName(item.status?.id) }}
              </span>
            </td>
            <td>
              <span
                v-if="item.last_status_change"
                :class="{ 'text-danger font-weight-bold': isStuck(item) }"
              >{{ formatAntiguedad(item.last_status_change) }}</span>
              <span v-else class="text-grey">-</span>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <!-- OPERARIOS -->
    <div class="section operarios-panel mb-4">
      <div class="section-header">
        <div>
          <h6 class="mb-0">Operarios activos</h6>
          <p class="text-caption text-grey mb-0">Carga de trabajo en tiempo real</p>
        </div>
        <span class="badge-turno">{{ activeOperatorsCount }} en turno</span>
      </div>

      <v-skeleton-loader v-if="loading_operators" type="list-item-avatar-two-line@3"></v-skeleton-loader>

      <div v-else-if="operators.length === 0" class="text-grey text-caption pa-2">
        Sin operarios activos
      </div>
      <div v-else class="operarios-list">
        <div v-for="op in operators" :key="op.user_id" class="operario-item">
          <div class="operator-avatar mr-2" :style="getIconStyle(op.user_name)">
            <span class="operator-initials">{{ getInitials(op.user_name) }}</span>
          </div>
          <div class="operario-info">
            <div class="operario-name">{{ op.user_name }}</div>
            <div class="text-caption text-grey">
              {{ op.total_tasks > 0 ? `Pickeando · ${op.total_tasks} tareas` : 'Sin tarea asignada' }}
            </div>
          </div>
          <div class="operario-tasks">
            <div class="operario-progress-bar" v-if="op.total_tasks > 0">
              <div
                class="operario-progress-fill"
                :style="{ width: getTaskProgress(op) + '%', backgroundColor: getProgressColor(op) }"
              ></div>
            </div>
            <span v-if="op.total_tasks > 0" class="text-caption">{{ op.total_tasks }} tareas</span>
            <span v-else class="text-caption text-grey">&mdash;</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { shipmentOrderService } from "@/libs/ws/shipmentOrderService";

const OPEN_STATES = [
  shipmentOrderService.STATUS_PENDIENTE_PREPARACION,
  shipmentOrderService.STATUS_STOCK_RESERVADO,
  shipmentOrderService.STATUS_EN_PREPARACION,
  shipmentOrderService.STATUS_PREPARADO,
  shipmentOrderService.STATUS_EMPAQUETADO,
];

export default {
  props: {
    status_shipments:        { type: Array,   default: () => [] },
    loading_shipment_status: { type: Boolean, default: false },
    loading_shipment_today:  { type: Boolean, default: false },
    shipment_orders:         { type: Array,   default: () => [] },
    stuck_hours:             { type: Number,  default: 48 },
    days_back:               { type: Number,  default: 30 },
    operators:               { type: Array,   default: () => [] },
    loading_operators:       { type: Boolean, default: false },
  },
  emits: ["stuck-count"],
  data() {
    return {
      shipmentOrderService,
      activeStateFilter: null,
      filterStuck: false,
      headers: [
        { title: "Código",         key: "code" },
        { title: "Cliente",        key: "client.user.username" },
        { title: "Documento / Ref.", key: "document_type",    sortable: false },
        { title: "Canal de venta", key: "canal_venta",        sortable: false },
        { title: "Estado",         key: "status",             sortable: false },
        { title: "En estado hace", key: "last_status_change", sortable: false },
      ],
    };
  },
  computed: {
    stuckThresholdLabel() {
      const h = this.stuck_hours;
      if (h < 24) return `${h}h`;
      const days = Math.floor(h / 24);
      const rem  = h % 24;
      return rem > 0 ? `${days}d ${rem}h` : `${days} ${days === 1 ? "día" : "días"}`;
    },
    pipelineStates() {
      return this.status_shipments.filter((s) => OPEN_STATES.includes(s.id_state));
    },
    activeOrders() {
      return this.shipment_orders.filter((o) => OPEN_STATES.includes(o.status?.id));
    },
    stuckOrders() {
      return this.activeOrders.filter(
        (o) => o.last_status_change && this.diffWorkingHours(o.last_status_change) > this.stuck_hours
      );
    },
    filteredActiveOrders() {
      if (this.filterStuck) return this.stuckOrders;
      if (this.activeStateFilter !== null)
        return this.activeOrders.filter((o) => o.status?.id === this.activeStateFilter);
      return this.activeOrders;
    },
    activeOperatorsCount() {
      return this.operators.filter((o) => o.is_active).length;
    },
  },
  watch: {
    stuckOrders(val) {
      this.$emit("stuck-count", val.length);
    },
  },
  methods: {
    getFilterBtnStyle(stateId, isActive) {
      const states = {
        PEN: { color: '#E65100', border: '#FFC107', bg: '#FFF8E1' },
        STR: { color: '#BF360C', border: '#FF5722', bg: '#FBE9E7' },
        EPR: { color: '#0D47A1', border: '#2196F3', bg: '#E3F2FD' },
        PRE: { color: '#004877', border: '#004877', bg: '#E1ECF4' },
        EMP: { color: '#00695C', border: '#009688', bg: '#E0F2F1' },
      };
      const s = states[stateId];
      if (!s) return {};
      return isActive
        ? { backgroundColor: s.border, borderColor: s.border, color: 'white' }
        : {};
    },
    ordersCountByState(stateId) {
      return this.activeOrders.filter((o) => o.status?.id === stateId).length;
    },
    isStuck(item) {
      return !!item.last_status_change && this.diffWorkingHours(item.last_status_change) > this.stuck_hours;
    },
    pipelineCardName(state) {
      const { STATUS_PENDIENTE_PREPARACION, STATUS_STOCK_RESERVADO,
              STATUS_EN_PREPARACION, STATUS_PREPARADO, STATUS_EMPAQUETADO,
              STATUS_EXPEDIDO, STATUS_CANCELADO } = shipmentOrderService;
      const names = {
        [STATUS_PENDIENTE_PREPARACION]: "PEND. RESERVA",
        [STATUS_STOCK_RESERVADO]:       "PEND. PREPARACIÓN",
        [STATUS_EN_PREPARACION]:        "EN PREPARACIÓN",
        [STATUS_PREPARADO]:             "PREPARADO",
        [STATUS_EMPAQUETADO]:           "EMPAQUETADO",
        [STATUS_EXPEDIDO]:              "EXPEDIDO",
        [STATUS_CANCELADO]:             "CANCELADO",
      };
      return names[state.id_state] ?? state.state.toUpperCase();
    },
    getInitials(name) {
      if (!name) return "??";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return parts[0].slice(0, 2).toUpperCase();
    },
    getIconStyle(name) {
      const colors = ["#1976D2", "#388E3C", "#D32F2F", "#7B1FA2", "#F57C00", "#0097A7", "#C2185B", "#5D4037", "#455A64", "#E64A19"];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return { backgroundColor: colors[Math.abs(hash) % colors.length] };
    },
    getTaskProgress(op) {
      if (!op.total_tasks) return 0;
      return Math.round((op.tasks_completed / op.total_tasks) * 100);
    },
    getProgressColor(op) {
      const p = this.getTaskProgress(op);
      if (p >= 80) return "#4CAF50";
      if (p >= 40) return "#2196F3";
      return "#FFC107";
    },
    diffWorkingHours(dateStr) {
      if (!dateStr) return 0;
      const then = new Date(dateStr);
      const now  = new Date();
      if (then >= now) return 0;
      let hours = 0;
      const cursor = new Date(then);
      while (cursor < now) {
        const next = new Date(cursor);
        next.setHours(cursor.getHours() + 1);
        const end = next < now ? next : now;
        if (cursor.getDay() !== 0) hours += (end - cursor) / 1000 / 3600;
        cursor.setTime(end.getTime());
      }
      return hours;
    },
    formatAntiguedad(dateStr) {
      if (!dateStr) return "-";
      const diff = this.diffWorkingHours(dateStr);
      if (diff < 1) return `hace ${Math.round(diff * 60)} min`;
      if (diff < 24) return `hace ${Math.floor(diff)}h`;
      const days  = Math.floor(diff / 24);
      const hours = Math.floor(diff % 24);
      return `${days}d ${hours}h`;
    },
  },
};
</script>
